import type { AeEvaluatedQuestion } from '@/types/assessmentEngine';
import type { QuestionAnswerValue } from './QuestionRenderer';
import { toBuilderQuestionType } from './template-builder/questionTypeConfig';

/**
 * Whether a required question has a valid response.
 * Optional questions always pass. N/A counts when allow_na is enabled.
 */
export function isRequiredQuestionAnswered(
  question: AeEvaluatedQuestion,
  value: QuestionAnswerValue | undefined
): boolean {
  if (!question.is_required) return true;

  const v = value ?? {};
  if (v.is_na && question.allow_na) return true;

  const builderType = toBuilderQuestionType(question.question_type);

  if (builderType === 'scale' || builderType === 'nps') {
    return v.answer_numeric != null && !Number.isNaN(v.answer_numeric);
  }

  if (builderType === 'matrix') {
    const matrix = v.answer_matrix ?? {};
    if (question.options.length === 0) return false;
    return question.options.every((opt) => matrix[opt.option_value] != null);
  }

  if (builderType === 'multi_choice') {
    return Array.isArray(v.answer_values) && v.answer_values.length > 0;
  }

  if (
    builderType === 'short_text' ||
    builderType === 'long_text' ||
    builderType === 'single_choice' ||
    question.question_type === 'demographic'
  ) {
    return typeof v.answer_text === 'string' && v.answer_text.trim().length > 0;
  }

  return false;
}

export function unansweredRequiredQuestions(
  questions: AeEvaluatedQuestion[],
  answersByQuestionId: Record<string, QuestionAnswerValue | undefined>
): AeEvaluatedQuestion[] {
  return questions.filter(
    (q) => q.is_required && !isRequiredQuestionAnswered(q, answersByQuestionId[q.id])
  );
}
